import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import axios from "axios";
import { getApiResponseErrorMsg } from "@funded-here-interface/common/src/Utils/apiHelper";

export interface SubscriptionArgeementPostData {
  structuredNoteId: number;
  token: string;
  investorSubscribedAmount: string;
}

export interface SubscriptionResponse {
  message: string;
  requestedAmount: string;
  subscribedAmount: string;
  isPartialSubscription: boolean;
  currency: "SGD" | "USD";
  remainingAvailable: string;
}

/**
 * Error response data from subscription API
 * Used for 409 CONFLICT and other error responses
 */
export interface SubscriptionErrorData {
  code?: string;
  message?: string;
  requestedAmount?: string;
  availableAmount?: string;
  currency?: string;
}

/**
 * Custom error class for subscription errors
 * Preserves full error response data including error code
 */
export class SubscriptionError extends Error {
  code?: string;
  requestedAmount?: string;
  availableAmount?: string;
  currency?: string;

  constructor(message: string, errorData?: SubscriptionErrorData) {
    super(message);
    this.name = "SubscriptionError";

    if (errorData) {
      this.code = errorData.code;
      this.requestedAmount = errorData.requestedAmount;
      this.availableAmount = errorData.availableAmount;
      this.currency = errorData.currency;
    }
  }
}

const postSubscriptionAgreementSigned = async ({
  token,
  structuredNoteId,
  investorSubscribedAmount,
}: SubscriptionArgeementPostData): Promise<SubscriptionResponse> => {
  try {
    const formattedInvestorSubscribedAmount = parseFloat(
      investorSubscribedAmount
    );

    const response = await axios.post<SubscriptionResponse>(
      `${getBaseUrl(Microservice.SKU_FINANCE)}/sku-finance/sn/subscription-agreement-signed`,
      {
        structuredNoteId,
        investorSubscribedAmount: formattedInvestorSubscribedAmount,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      }
    );

    return response.data;
  } catch (e) {
    // Preserve full error response for 409 and other API errors
    if (axios.isAxiosError(e) && e.response?.data) {
      const errorData = e.response.data;
      const message = errorData.message || getApiResponseErrorMsg(e);
      throw new SubscriptionError(message, errorData);
    }

    const message = getApiResponseErrorMsg(e);
    throw new Error(message);
  }
};

export default postSubscriptionAgreementSigned;
